<script setup lang="ts">
import type { AttendeeData } from '@/types/attendee';
import type { AvatarSize } from '@/types/avatar';

const props = defineProps({
	limit: {
		type: Number,
		default: 3,
	},
	seatSoldCount: {
		type: Number as PropType<number | null>,
		default: null,
	},
	attendees: {
		type: Array as PropType<AttendeeData[]>,
		default: () => [],
	},
	size: {
		type: String as PropType<AvatarSize>,
		default: AvatarSizes[2],
	},
	isShowAttendeesCount: {
		type: Boolean,
		default: false,
	},
});

const attendeesCount = computed(() => {
	return props.attendees.length;
});

// Keep an internal snapshot of attendees.
// Behavior:
// - Stable across pagination if the store mutates the same array (reference unchanged)
// - Refresh snapshot when the attendees array reference changes (e.g., after a full refetch)
const snapshotAttendees = ref<AttendeeData[] | null>(null);

// Attendees to render as avatars (excluding the counter circle)
const visibleAttendees = computed(() => {
	const base = snapshotAttendees.value ?? props.attendees;
	return base.slice(0, props.limit);
});

const counterValue = computed(() => {
	// Only show a numeric counter when we've filled up to the limit of visible avatars
	// If fewer attendees than the limit, show just the '+' without a number
	if (!props.seatSoldCount || attendeesCount.value < props.limit) {
		return 0;
	}
	// We render up to `limit` avatars, so the remaining count is seatSoldCount minus those visible avatars
	return Math.max(0, props.seatSoldCount - props.limit);
});

// Reset snapshot when the attendees array reference changes
watch(
	() => props.attendees,
	(newVal, oldVal) => {
		if (newVal !== oldVal && newVal && newVal.length) {
			// Update snapshot only when we have a non-empty new array
			snapshotAttendees.value = newVal.slice();
		} else if (!snapshotAttendees.value && newVal && newVal.length) {
			// Initial population when snapshot is empty
			snapshotAttendees.value = newVal.slice();
		}
	},
	{ immediate: true, deep: false },
);
</script>

<template>
	<div class="avatar-stack">
		<div
			v-for="(att, idx) in visibleAttendees"
			:key="`${idx}-${att.id}`"
			class="avatar-item"
			:style="{ zIndex: 1 + idx }"
		>
			<AvatarImage :image-url="att.avatar" :alt="att.nickname" :size="size" />
			<VTooltip activator="parent" location="bottom left">
				<div class="tooltip">
					{{ att.nickname }}
				</div>
			</VTooltip>
		</div>

		<div
			class="avatar-item avatar-counter avatar-counter-text avatar"
			:class="`size-${size}`"
			:style="{ zIndex: 1 + visibleAttendees.length }"
			aria-label="more participants"
		>
			<span>+</span>
			<span v-if="counterValue && props.isShowAttendeesCount">{{ counterValue }}</span>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.avatar-stack {
	width: fit-content;
	display: flex;
	align-items: center;

	// Overlap from left to right: subsequent items shift left
	.avatar-item {
		position: relative;
		// create a subtle ring to separate overlapped avatars
		padding: 2px;
		background: colors-get(base, white);
		border-radius: 50%;

		&:not(:first-child) {
			margin-left: -#{spacings-get(2)}; // negative gap to overlap
		}

		// Ensure the AvatarImage itself remains circular and fills container
		:deep(.avatar) {
			display: block;
		}
	}

	.avatar-counter {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		aspect-ratio: 1;
		background: colors-get(gray, 100);
		border: 1px solid colors-get(gray, 200);
		user-select: none;
	}

	.avatar-counter-text {
		color: colors-get(gray, 600);
		@include fonts-get(regular, text-xs);
	}
}

// Local avatar size classes to match AvatarImage.vue
.avatar {
	aspect-ratio: 1;
	border-radius: 50%;

	&.size-xs {
		height: rem(24);
	}
	&.size-sm {
		height: rem(32);
	}
	&.size-md {
		height: rem(40);
	}
	&.size-lg {
		height: rem(48);
	}
	&.size-xl {
		height: rem(56);
	}
	&.size-2xl {
		height: rem(64);
	}
}
</style>
